import React, { useEffect, useState } from "react";
import { Modal, Button, Input, message, Space } from "antd";
import {
  CopyOutlined,
  CheckCircleOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  WhatsAppOutlined,
  SendOutlined
} from "@ant-design/icons";

/* ================= TYPES ================= */

export interface UserCreatedData {
  userName: string;
  password: string;
  mobileNumber: string;
}

interface Props {
  open: boolean;
  data: UserCreatedData | null;
  onClose: () => void;

  /** optional */
  autoCloseSeconds?: number;
  locale?: "en" | "hi";
  onAuditLog?: (data: UserCreatedData) => void;
}

/* ================= I18N ================= */

const TEXT = {
  en: {
    title: "User Registered Successfully",
    username: "Username",
    password: "Password",
    mobile: "Mobile",
    copyAll: "Copy All",
    close: "Close",
    copied: "Copied to clipboard",
    share: "Share"
  },
  hi: {
    title: "उपयोगकर्ता सफलतापूर्वक बनाया गया",
    username: "यूज़रनेम",
    password: "पासवर्ड",
    mobile: "मोबाइल",
    copyAll: "सभी कॉपी करें",
    close: "बंद करें",
    copied: "कॉपी किया गया",
    share: "शेयर करें"
  }
};

/* ================= COMPONENT ================= */

const UserCreatedModal: React.FC<Props> = ({
  open,
  data,
  onClose,
  autoCloseSeconds = 30,
  locale = "en",
  onAuditLog
}) => {
  const t = TEXT[locale];

  const [showPassword, setShowPassword] = useState(false);
  const [maskedPassword, setMaskedPassword] = useState<string>("");

  /* Mask password initially */
  useEffect(() => {
    if (data?.password) {
      setMaskedPassword("•".repeat(data.password.length));
    }
  }, [data]);

  /* Auto close */
  useEffect(() => {
    if (!open) return;

    const timer = setTimeout(() => {
      onClose();
    }, autoCloseSeconds * 1000);

    return () => clearTimeout(timer);
  }, [open, autoCloseSeconds, onClose]);

  /* Audit log */
  useEffect(() => {
    if (open && data && onAuditLog) {
      onAuditLog(data);
    }
  }, [open, data, onAuditLog]);

  if (!data) return null;

  const copyToClipboard = (text: string, maskPassword?: boolean) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(t.copied);
      if (maskPassword) {
        setShowPassword(false);
      }
    });
  };

  const shareText = `Username: ${data.userName}
    Password: ${data.password}
    Mobile: ${data.mobileNumber}`;

    const whatsappUrl = `https://wa.me/${data.mobileNumber}?text=${encodeURIComponent(
        shareText
    )}`;

    const formattedMobile = data.mobileNumber.startsWith("91")
  ? data.mobileNumber
  : `${data.mobileNumber.replace(/\+/g, "")}`;

   const whatsappNo = formattedMobile.startsWith("91")
    ? formattedMobile
    : `91${formattedMobile}`;
    console.log('formattedMobile' + formattedMobile);
    console.log('whatsappNo' + whatsappNo);
    console.log(data.mobileNumber)
    const encodedText = encodeURIComponent(shareText);

    const appUrl = `whatsapp://send?phone=${data.mobileNumber}&text=${encodedText}`;
    const webUrl = `https://wa.me/${data.mobileNumber}?text=${encodedText}`;

  //const closePopover = () => setOpen(false);
   const openWhatsApp = () => {
    // Try WhatsApp App
        window.location.href = appUrl;

        // Fallback to WhatsApp Web
        setTimeout(() => {
            window.open(webUrl, "_blank", "noopener,noreferrer");
        }, 1500);
    };


    const smsUrl = `sms:${data.mobileNumber}?body=${encodeURIComponent(
        shareText
    )}`;

  return (
    <Modal
      open={open}
      centered
      footer={null}
      onCancel={onClose}
      title={
        <span>
          <CheckCircleOutlined style={{ color: "green", marginRight: 8 }} />
          {t.title}
        </span>
      }
    >
      {/* Username */}
      <Input
        value={data.userName}
        addonBefore={t.username}
        readOnly
        addonAfter={
          <CopyOutlined
            onClick={() => copyToClipboard(data.userName)}
            style={{ cursor: "pointer" }}
          />
        }
        style={{ marginBottom: 10 }}
      />

      {/* Password */}
      <Input
        value={showPassword ? data.password : maskedPassword}
        addonBefore={t.password}
        readOnly
        addonAfter={
          <Space>
            {showPassword ? (
              <EyeInvisibleOutlined
                onClick={() => setShowPassword(false)}
                style={{ cursor: "pointer" }}
              />
            ) : (
              <EyeOutlined
                onClick={() => setShowPassword(true)}
                style={{ cursor: "pointer" }}
              />
            )}
            <CopyOutlined
              onClick={() => copyToClipboard(data.password, true)}
              style={{ cursor: "pointer" }}
            />
          </Space>
        }
        style={{ marginBottom: 10 }}
      />

      {/* Mobile */}
      <Input
        value={data.mobileNumber}
        addonBefore={t.mobile}
        readOnly
        addonAfter={
          <CopyOutlined
            onClick={() => copyToClipboard(data.mobileNumber)}
            style={{ cursor: "pointer" }}
          />
        }
      />

      {/* Actions */}
      <Space style={{ marginTop: 20, display: "flex", justifyContent: "space-between" }}>
        <Button
          type="primary"
          onClick={() => copyToClipboard(shareText)}
        >
          {t.copyAll}
        </Button>

        <Space>
          <Button
            icon={<WhatsAppOutlined  style={{ color: "green" }} />}
            onClick={openWhatsApp}
          >
            WhatsApp
          </Button>
     
          <Button icon={<SendOutlined />} href={smsUrl}>
            SMS
          </Button>

          <Button onClick={onClose}>{t.close}</Button>
        </Space>
      </Space>
    </Modal>
  );
};

export default UserCreatedModal;
